import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  colors,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import DeleteIcon from "@material-ui/icons/Delete";
import WarningIcon from "@material-ui/icons/Warning";
import { makeStyles } from "@material-ui/styles";
import clsx from "clsx";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useTranslation } from "react-i18next";
import HelpIcon from "@material-ui/icons/Help";
import { getFacility } from "src/repos/facilities";
import { getWebhooks } from "src/repos/webhooks";

const useStyles = makeStyles((theme) => ({
  root: {},
  chip: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  container: {
    padding: 0,
  },
  keywords: {
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
  },
  searchIcon: {
    color: theme.palette.icon,
    marginRight: theme.spacing(2),
  },
  chips: {
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
  },
  select: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    padding: theme.spacing(1),
  },
  tagLabel: {
    marginLeft: "10px",
    fontWeight: "700",
    fontSize: "14px",
    marginTop: "15px",
  },
  formControl: {
    width: "100%",
  },
  button: {
    backgroundColor: colors.red[700],
    "&:hover": {
      backgroundColor: colors.red[900],
    },
  },
  colorGray: {
    color: "gray",
  },
  notificationFormControl: {
    width: "100%",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

// Convert local hours to UTC hours
const convertLocalToUTC = (localHours) => {
  const now = new Date();
  return localHours.map((localHour) => {
    const localDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      localHour
    );
    const utcHour = localDate.getUTCHours();
    return utcHour;
  });
};

// Convert UTC hours to local hours for display
const convertUTCToLocal = (utcHours) => {
  const now = new Date();
  return utcHours.map((utcHour) => {
    const utcDate = new Date(
      Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), utcHour)
    );
    const localHour = utcDate.getHours();
    return localHour;
  });
};

function AlarmForm({
  machine,
  alarm,
  availableMetrics,
  machineStates,
  emailList,
  onCreateAlarm,
  onUpdateAlarm,
  onDeleteAlarm,
  className,
  ...rest
}) {
  const { t } = useTranslation(["glossary", "common"]);

  const SECONDS_IN_WEEK = 604800;
  const SECONDS_IN_DAY = 86400;
  const SECONDS_IN_HOUR = 3600;
  const SECONDS_IN_MINUTE = 60;

  const THRESHOLD_ALARM = "value_threshold_for_time_period_in_state";
  const ANOMALY_ALARM = "anomalous_for_time_period";
  const CRON_AGG_ALARM = "cron_aggregate";

  const classes = useStyles();
  const [availableEmails, setAvailableEmails] = useState([]);
  const [webhooksList, setWebhooksList] = useState([]);
  const [selectedWebhooks, setSelectedWebhooks] = useState([]);
  const [enableWebhooks, setEnableWebhooks] = useState(false);

  // Get user's timezone
  const [userTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  const [currentAlarm, setCurrentAlarm] = useState({
    name: alarm?.name || "",
    function: alarm?.function || THRESHOLD_ALARM,
    extra_emails: alarm?.extra_emails || [],
    sap_notification: alarm?.sap_notification,
    sap_last_notified: null,
    sap_rate_in_seconds: alarm?.sap_rate_in_seconds || SECONDS_IN_WEEK,
  });

  const [alarmArgs, setAlarmArgs] = useState({
    machine_states: alarm?.arguments?.machine_states || [],
    comp: alarm?.arguments?.comp || ">=",
    tag_name: alarm?.arguments?.tag_name || "",
    period: alarm?.arguments?.period / 1000 || 0,
    threshold: alarm?.arguments?.threshold || 0,
    uts_ad_enabled: alarm.arguments?.uts_ad_enabled || false,
    threshold_ad_enabled: alarm.arguments?.threshold_ad_enabled || false,
    aggregation_window_hours: Math.floor(
      (alarm?.arguments?.aggregation_window_minutes || 60) / 60
    ), // Convert minutes to hours for display
    time_to_run: alarm?.arguments?.time_to_run || [9, 17], // Will store UTC hours
    machine_state: alarm?.arguments?.machine_state || "running",
    // Track local time for display
    local_time_to_run: [], // Will store local hours for display
  });

  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const isNew = alarm?.id === undefined;
  const title = isNew ? t("create_alarm") : t("alarm_configuration");

  const closeDeleteConfirm = () => setOpenDeleteConfirm(false);

  const [enableSapNotification, setEnableSapNotification] = useState(false);
  const [sapNotificationFields, setSapNotificationFields] = useState({});
  const [notificationRateUnit, setNotificationRateUnit] = useState("days");
  const [notificationRateError, setNotificationRateError] = useState("");
  const [webhookPayloadValues, setWebhookPayloadValues] = useState("");
  const [jsonError, setJsonError] = useState("");

  const [SAPEnabled, setSAPEnabled] = useState(false);

  useEffect(() => {
    const SAPEnabledCheck = async () => {
      try {
        const response = await getFacility(machine.facility_id);
        if (!response.ok) throw new Error("Network response was not ok");
        const facility = await response.json();
        const SAPProtocol = facility?.data?.sap_protocol;
        setSAPEnabled(SAPProtocol !== undefined && SAPProtocol !== null);
      } catch (error) {
        console.error("Failed to fetch facility:", error);
        setSAPEnabled(false);
      }
    };
    SAPEnabledCheck();
  }, [machine.facility_id]);

  useEffect(() => {
    const fetchWebhooks = async (customerId) => {
      if (customerId) {
        getWebhooks({ customer_id: customerId })
          .then((r) => r.json())
          .then(({ data }) => setWebhooksList(data || []));
      } else {
        getWebhooks()
          .then((r) => r.json())
          .then(({ data }) => setWebhooksList(data || []));
      }
    };

    if (alarm?.customer_id) {
      fetchWebhooks(alarm.customer_id);
    } else if (machine?.facility_id) {
      getFacility(machine.facility_id)
        .then((r) => r.json())
        .then((facilityData) => {
          const customerId = facilityData?.data?.customer_id;
          fetchWebhooks(customerId);
        })
        .catch((err) => {
          console.error("Failed to fetch facility:", err);
          fetchWebhooks(null);
        });
    } else {
      fetchWebhooks(null);
    }
  }, [alarm, machine]);

  // Initialize form from alarm data
  useEffect(() => {
    let converted_alarm_rate = alarm?.sap_rate_in_seconds || SECONDS_IN_WEEK;
    if (converted_alarm_rate % SECONDS_IN_DAY === 0) {
      setNotificationRateUnit("days");
      converted_alarm_rate /= SECONDS_IN_DAY;
    } else if (converted_alarm_rate % SECONDS_IN_HOUR === 0) {
      setNotificationRateUnit("hours");
      converted_alarm_rate /= SECONDS_IN_HOUR;
    } else {
      setNotificationRateUnit("minutes");
      converted_alarm_rate /= SECONDS_IN_MINUTE;
    }

    setCurrentAlarm({
      name: alarm?.name || "",
      function: alarm?.function || THRESHOLD_ALARM,
      extra_emails: alarm?.extra_emails || [],
      sap_notification: alarm?.sap_notification || null,
      sap_last_notified: null,
      sap_rate_in_seconds: converted_alarm_rate,
    });

    // Convert stored UTC times to local for display
    const storedTimeToRun = alarm?.arguments?.time_to_run || [9, 17];
    const localTimeToRun = convertUTCToLocal(storedTimeToRun);

    setAlarmArgs({
      machine_states: alarm?.arguments?.machine_states || [],
      comp: alarm?.arguments?.comp || ">=",
      tag_name: alarm?.arguments?.tag_name || "",
      period: alarm?.arguments?.period / 1000 || 0,
      threshold: alarm.arguments?.threshold || 0,
      uts_ad_enabled: alarm.arguments?.uts_ad_enabled || false,
      threshold_ad_enabled: alarm.arguments?.threshold_ad_enabled || false,
      aggregation_window_hours: Math.floor(
        (alarm?.arguments?.aggregation_window_minutes || 60) / 60
      ),
      time_to_run: storedTimeToRun,
      machine_state: alarm?.arguments?.machine_state || "running",
      local_time_to_run: localTimeToRun,
    });

    if (alarm?.webhooks && alarm.webhooks.length > 0) {
      setEnableWebhooks(true);
      setSelectedWebhooks(alarm.webhooks);
    } else {
      setEnableWebhooks(false);
      setSelectedWebhooks([]);
    }

    // Load custom webhook payload values
    if (alarm?.webhook_payload_values) {
      try {
        setWebhookPayloadValues(
          JSON.stringify(alarm.webhook_payload_values, null, 2)
        );
        setJsonError("");
      } catch (e) {
        setWebhookPayloadValues("");
      }
    } else {
      setWebhookPayloadValues("");
    }

    if (
      alarm.sap_notification === null ||
      alarm.sap_notification === undefined
    ) {
      setEnableSapNotification(false);
    } else {
      setEnableSapNotification(true);
    }

    setSapNotificationFields({
      NotifType: "M4",
      ShortText: alarm.sap_notification?.ShortText || "",
      Priority: "2",
      FunctLoc: alarm.sap_notification?.FunctLoc || "",
      Reportedby: "Helios",
      CodeGroup: "IP3",
      Coding: "0160",
      Breakdown: false,
      Status: "E0010",
    });
  }, [alarm]);

  useEffect(() => {
    setAvailableEmails(emailList);
  }, [emailList]);

  // Recalculate run times when aggregation window changes
  useEffect(() => {
    if (
      currentAlarm.function === CRON_AGG_ALARM &&
      alarmArgs.local_time_to_run?.length > 0 &&
      alarmArgs.aggregation_window_hours
    ) {
      const selectedLocalHour = alarmArgs.local_time_to_run[0];
      const aggregationWindow = alarmArgs.aggregation_window_hours;
      const allLocalHours = [];
      for (let i = 0; i < 24; i += aggregationWindow) {
        const hour = (selectedLocalHour + i) % 24;
        allLocalHours.push(hour);
      }
      const allUtcHours = convertLocalToUTC(allLocalHours);
      setAlarmArgs((prev) => ({
        ...prev,
        time_to_run: allUtcHours,
      }));
    }
  }, [
    alarmArgs.aggregation_window_hours,
    alarmArgs.local_time_to_run,
    currentAlarm.function,
  ]);

  const handleFormChange = (event) => {
    event.persist();
    setCurrentAlarm({
      ...currentAlarm,
      [event.target.name]: event.target.value,
    });
  };

  const handleNotificationRateInput = (event) => {
    const value = event.target.value.replace(/[^0-9]/g, "");
    if (value === "0") {
      setNotificationRateError(t("notification_rate_error"));
    } else {
      setNotificationRateError("");
    }
    setCurrentAlarm({
      ...currentAlarm,
      [event.target.name]: value,
    });
  };

  const handleArgChange = (event) => {
    event.persist();
    setAlarmArgs({
      ...alarmArgs,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (notificationRateError) return;

    if (
      currentAlarm.function === ANOMALY_ALARM &&
      !alarmArgs.uts_ad_enabled &&
      !alarmArgs.threshold_ad_enabled
    ) {
      alert("Please enable at least one Anomaly Detection Type");
      return;
    }

    // Prepare arguments based on alarm type
    let args = {};
    if (currentAlarm.function === THRESHOLD_ALARM) {
      args = {
        machine_states: alarmArgs.machine_states,
        comp: alarmArgs.comp,
        tag_name: alarmArgs.tag_name,
        period: alarmArgs.period * 1000,
        threshold: parseFloat(alarmArgs.threshold),
      };
    } else if (currentAlarm.function === CRON_AGG_ALARM) {
      // Convert hours to minutes for backend
      const aggregationWindowMinutes = alarmArgs.aggregation_window_hours * 60;
      args = {
        tag_name: alarmArgs.tag_name,
        comp: alarmArgs.comp,
        threshold_value: parseFloat(alarmArgs.threshold),
        aggregation_window_minutes: aggregationWindowMinutes,
        time_to_run: alarmArgs.time_to_run,
        machine_state: alarmArgs.machine_state,
      };
    } else if (currentAlarm.function === ANOMALY_ALARM) {
      args = {
        tag_name: alarmArgs.tag_name,
        period: alarmArgs.period * 1000,
        uts_ad_enabled: alarmArgs.uts_ad_enabled,
        threshold_ad_enabled: alarmArgs.threshold_ad_enabled,
      };
    }

    if (!enableSapNotification) {
      currentAlarm.sap_notification = null;
    } else {
      try {
        if (sapNotificationFields.ShortText === "") {
          sapNotificationFields.ShortText = currentAlarm.name;
        }
        currentAlarm.sap_notification = { ...sapNotificationFields };
      } catch (error) {
        return;
      }
    }

    // Convert notification rate to seconds
    switch (notificationRateUnit) {
      case "minutes":
        currentAlarm.sap_rate_in_seconds *= SECONDS_IN_MINUTE;
        break;
      case "hours":
        currentAlarm.sap_rate_in_seconds *= SECONDS_IN_HOUR;
        break;
      case "days":
        currentAlarm.sap_rate_in_seconds *= SECONDS_IN_DAY;
        break;
      default:
    }

    const webhook_ids = enableWebhooks ? selectedWebhooks.map((w) => w.id) : [];

    let parsedWebhookPayload = null;
    if (webhookPayloadValues.trim() !== "") {
      try {
        parsedWebhookPayload = JSON.parse(webhookPayloadValues);
      } catch (e) {
        setJsonError(t("invalid_json"));
        return;
      }
    }

    const alarmData = {
      ...currentAlarm,
      arguments: args,
      device_id: machine.id,
      webhook_ids,
      webhook_payload_values: parsedWebhookPayload,
    };

    if (currentAlarm.function === CRON_AGG_ALARM) {
      alarmData.aggregation_window_minutes =
        alarmArgs.aggregation_window_hours * 60;
      alarmData.time_to_run = alarmArgs.time_to_run;
      alarmData.timezone = "UTC";
      alarmData.scheduling_enabled = true;
      alarmData.alarm_type = "cron_aggregate";
    }

    isNew
      ? onCreateAlarm(alarmData)
      : onUpdateAlarm({ ...alarmData, id: alarm.id });
  };

  const handleSapCheckboxChange = (event) => {
    setEnableSapNotification(event.target.checked);
  };

  const handleUtsAnomalyCheckboxChange = (event) => {
    setAlarmArgs({ ...alarmArgs, uts_ad_enabled: event.target.checked });
  };

  const handleThresholdAnomalyCheckboxChange = (event) => {
    setAlarmArgs({ ...alarmArgs, threshold_ad_enabled: event.target.checked });
  };

  const handleFieldChange = (fieldName, value) => {
    setSapNotificationFields({ ...sapNotificationFields, [fieldName]: value });
  };

  return (
    <Card {...rest} className={clsx(classes.root, className)}>
      <CardHeader
        title={title}
        action={
          !isNew ? (
            <IconButton
              aria-label="delete"
              onClick={() => setOpenDeleteConfirm(true)}
              style={{ marginTop: -5, marginBottom: -15 }}
            >
              <DeleteIcon />
            </IconButton>
          ) : null
        }
      />
      <Divider />
      <form onSubmit={handleSubmit}>
        <CardContent>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={12}>
              <FormControl className={classes.formControl}>
                <TextField
                  label={t("alarm_name")}
                  variant="outlined"
                  id="name"
                  name="name"
                  required
                  value={currentAlarm?.name}
                  onChange={handleFormChange}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl className={classes.formControl}>
                <InputLabel required id="alarm_type">
                  {t("alarm_type")}
                </InputLabel>
                <Tooltip
                  PopperProps={{ style: { zIndex: 1 } }}
                  title={t("anomaly_alarm_tooltip")}
                >
                  <Select
                    variant="outlined"
                    color="primary"
                    labelId="alarm-label"
                    id="function"
                    name="function"
                    required
                    style={{ width: "100%" }}
                    value={currentAlarm?.function}
                    onChange={handleFormChange}
                  >
                    <MenuItem value={THRESHOLD_ALARM}>
                      {t("threshold")}
                    </MenuItem>
                    <MenuItem value={ANOMALY_ALARM}>{t("anomaly")}</MenuItem>
                    <MenuItem value={CRON_AGG_ALARM}>{t("cron_agg")}</MenuItem>
                  </Select>
                </Tooltip>
              </FormControl>
            </Grid>
            {currentAlarm.function === ANOMALY_ALARM && (
              <>
                <Grid item xs={12} sm={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="uts_ad_enabled"
                        checked={alarmArgs?.uts_ad_enabled}
                        onChange={handleUtsAnomalyCheckboxChange}
                        value={alarmArgs?.uts_ad_enabled}
                      />
                    }
                    label={
                      <span>
                        {t("enable_uts_anomaly_detection")}
                        <Tooltip
                          title={t("enable_uts_anomaly_detection_tooltip")}
                        >
                          <HelpIcon
                            style={{
                              display: "inline",
                              fontSize: 16,
                              marginLeft: 3,
                            }}
                          />
                        </Tooltip>
                      </span>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={alarmArgs?.threshold_ad_enabled}
                        onChange={handleThresholdAnomalyCheckboxChange}
                      />
                    }
                    label={
                      <span>
                        {t("enable_threshold_anomaly_detection")}
                        <Tooltip
                          title={t(
                            "enable_threshold_anomaly_detection_tooltip"
                          )}
                        >
                          <HelpIcon
                            style={{
                              display: "inline",
                              fontSize: 16,
                              marginLeft: 3,
                            }}
                          />
                        </Tooltip>
                      </span>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl className={classes.formControl}>
                    <Tooltip title={t("and_tag_tooltip")}>
                      <Autocomplete
                        id="tag_name"
                        name="tag_name"
                        required
                        limitTags={2}
                        options={availableMetrics}
                        onChange={(event, newValue) => {
                          setAlarmArgs({ ...alarmArgs, tag_name: newValue });
                        }}
                        value={alarmArgs?.tag_name}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required
                            variant="outlined"
                            label={t("tag")}
                          />
                        )}
                      />
                    </Tooltip>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl className={classes.formControl}>
                    <Tooltip title={t("for_at_least_tooltip")}>
                      <TextField
                        label={t("anomalous_for_at_least")}
                        variant="outlined"
                        id="period"
                        name="period"
                        type="number"
                        required
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {t("sec")}
                            </InputAdornment>
                          ),
                        }}
                        value={alarmArgs?.period}
                        onChange={handleArgChange}
                      />
                    </Tooltip>
                  </FormControl>
                </Grid>
              </>
            )}
            {/* THRESHOLD ALARM FIELDS */}
            {currentAlarm?.function === THRESHOLD_ALARM && (
              <>
                {/* Machine State */}
                <Grid item xs={12} sm={4}>
                  <FormControl className={classes.formControl}>
                    <InputLabel required id="machine_state">
                      {t("when_machine_state_is")}
                    </InputLabel>
                    <Tooltip
                      PopperProps={{ style: { zIndex: 1 } }}
                      title={t("when_machine_state_is_tooltip")}
                    >
                      <Select
                        variant="outlined"
                        color="primary"
                        labelId="alarm-label"
                        id="machine_state"
                        name="machine_states"
                        multiple
                        required
                        style={{ width: "100%" }}
                        value={alarmArgs?.machine_states}
                        onChange={handleArgChange}
                        renderValue={(selected) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {_.map(selected, (value) => (
                              <Chip
                                key={value}
                                label={t(value.toString()).toLowerCase()}
                              />
                            ))}
                          </Box>
                        )}
                      >
                        {_.map(machineStates, (value) => (
                          <MenuItem key={value} value={value}>
                            {t(value.toString()).toLowerCase()}
                          </MenuItem>
                        ))}
                      </Select>
                    </Tooltip>
                  </FormControl>
                </Grid>
                {/* and tag */}
                <Grid item xs={12} sm={4}>
                  <FormControl className={classes.formControl}>
                    <Tooltip title={t("and_tag_tooltip")}>
                      <Autocomplete
                        id="tag_name"
                        name="tag_name"
                        required
                        limitTags={2}
                        options={availableMetrics}
                        onChange={(event, newValue) => {
                          setAlarmArgs({ ...alarmArgs, tag_name: newValue });
                        }}
                        value={alarmArgs?.tag_name}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required
                            variant="outlined"
                            label={t("and_tag") + " "}
                          />
                        )}
                      />
                    </Tooltip>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl className={classes.formControl}>
                    <InputLabel required id="compare">
                      {t("is")}
                    </InputLabel>
                    <Tooltip
                      PopperProps={{ style: { zIndex: 1 } }}
                      title={t("is_tooltip")}
                    >
                      <Select
                        variant="outlined"
                        color="primary"
                        labelId="alarm-label"
                        id="compare"
                        name="comp"
                        required
                        style={{ width: "100%" }}
                        value={alarmArgs?.comp}
                        onChange={handleArgChange}
                      >
                        <MenuItem key=">" value=">">
                          {t("greater_than")}
                        </MenuItem>
                        <MenuItem key=">=" value=">=">
                          {t("greater_than/equal_to")}
                        </MenuItem>
                        <MenuItem key="<" value="<">
                          {t("less_than")}
                        </MenuItem>
                        <MenuItem key="<=" value="<=">
                          {t("less than/equal to")}
                        </MenuItem>
                        <MenuItem key="==" value="==">
                          {t("equal_to")}
                        </MenuItem>
                        <MenuItem key="!=" value="!=">
                          {t("not_equal_to")}
                        </MenuItem>
                      </Select>
                    </Tooltip>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl className={classes.formControl}>
                    <Tooltip title={t("value_tooltip")}>
                      <TextField
                        label={t("value")}
                        id="threshold"
                        name="threshold"
                        type="number"
                        variant="outlined"
                        required
                        value={alarmArgs?.threshold}
                        onChange={handleArgChange}
                      />
                    </Tooltip>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl className={classes.formControl}>
                    <Tooltip title={t("for_at_least_tooltip")}>
                      <TextField
                        label={t("for_at_least") + " "}
                        variant="outlined"
                        id="period"
                        name="period"
                        type="number"
                        required
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {t("sec")}
                            </InputAdornment>
                          ),
                        }}
                        value={alarmArgs?.period}
                        onChange={handleArgChange}
                      />
                    </Tooltip>
                  </FormControl>
                </Grid>
              </>
            )}
            {/* CRON AGGREGATE ALARM FIELDS */}
            {currentAlarm?.function === CRON_AGG_ALARM && (
              <>
                {/* Machine State */}
                <Grid item xs={12} sm={4}>
                  <FormControl className={classes.formControl}>
                    <InputLabel required id="machine_state">
                      {t("machine_state")}
                    </InputLabel>
                    <Tooltip
                      PopperProps={{ style: { zIndex: 1 } }}
                      title={t("machine_state_tooltip")}
                    >
                      <Select
                        variant="outlined"
                        color="primary"
                        labelId="machine-state-label"
                        id="machine_state"
                        name="machine_state"
                        required
                        style={{ width: "100%" }}
                        value={alarmArgs?.machine_state}
                        onChange={handleArgChange}
                      >
                        {_.map(machineStates, (value) => (
                          <MenuItem key={value} value={value}>
                            {t(value.toString()).toLowerCase()}
                          </MenuItem>
                        ))}
                      </Select>
                    </Tooltip>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl className={classes.formControl}>
                    <Tooltip title={t("and_tag_tooltip")}>
                      <Autocomplete
                        id="tag_name"
                        name="tag_name"
                        required
                        limitTags={2}
                        options={availableMetrics}
                        onChange={(event, newValue) => {
                          setAlarmArgs({ ...alarmArgs, tag_name: newValue });
                        }}
                        value={alarmArgs?.tag_name}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required
                            variant="outlined"
                            label={t("and_tag") + " "}
                          />
                        )}
                      />
                    </Tooltip>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl className={classes.formControl}>
                    <InputLabel required id="compare">
                      {t("is")}
                    </InputLabel>
                    <Tooltip
                      PopperProps={{ style: { zIndex: 1 } }}
                      title={t("is_tooltip")}
                    >
                      <Select
                        variant="outlined"
                        color="primary"
                        labelId="alarm-label"
                        id="compare"
                        name="comp"
                        required
                        style={{ width: "100%" }}
                        value={alarmArgs?.comp}
                        onChange={handleArgChange}
                      >
                        <MenuItem key=">" value=">">
                          {t("greater_than")}
                        </MenuItem>
                        <MenuItem key=">=" value=">=">
                          {t("greater_than/equal_to")}
                        </MenuItem>
                        <MenuItem key="<" value="<">
                          {t("less_than")}
                        </MenuItem>
                        <MenuItem key="<=" value="<=">
                          {t("less than/equal to")}
                        </MenuItem>
                        <MenuItem key="==" value="==">
                          {t("equal_to")}
                        </MenuItem>
                        <MenuItem key="!=" value="!=">
                          {t("not_equal_to")}
                        </MenuItem>
                      </Select>
                    </Tooltip>
                  </FormControl>
                </Grid>
                {/* value */}
                <Grid item xs={12} sm={4}>
                  <FormControl className={classes.formControl}>
                    <Tooltip title={t("value_tooltip")}>
                      <TextField
                        label={t("value")}
                        id="threshold"
                        name="threshold"
                        type="number"
                        variant="outlined"
                        required
                        value={alarmArgs?.threshold}
                        onChange={handleArgChange}
                      />
                    </Tooltip>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl className={classes.formControl}>
                    <InputLabel required id="aggregation_window">
                      {t("aggregation_window")}
                    </InputLabel>
                    <Tooltip title={t("aggregation_window_tooltip")}>
                      <Select
                        variant="outlined"
                        color="primary"
                        labelId="aggregation-window-label"
                        id="aggregation_window_hours"
                        name="aggregation_window_hours"
                        required
                        style={{ width: "100%" }}
                        value={alarmArgs?.aggregation_window_hours}
                        onChange={handleArgChange}
                      >
                        <MenuItem value={1}>1 hour</MenuItem>
                        <MenuItem value={8}>8 hours</MenuItem>
                        <MenuItem value={24}>24 hours</MenuItem>
                      </Select>
                    </Tooltip>
                  </FormControl>
                </Grid>
                {/* Time to Run */}
                <Grid item xs={12} sm={4}>
                  <FormControl className={classes.formControl}>
                    <Tooltip title={t("times_to_run_tooltip")}>
                      <Autocomplete
                        id="local_time_to_run"
                        options={Array.from({ length: 24 }, (_, i) => i)}
                        getOptionLabel={(option) =>
                          `${option}:00 ${userTimezone}`
                        }
                        value={
                          alarmArgs?.local_time_to_run?.[0] !== undefined
                            ? [alarmArgs.local_time_to_run[0]]
                            : []
                        }
                        onChange={(event, newLocalHours) => {
                          const selectedLocalHour =
                            newLocalHours.length > 0 ? newLocalHours[0] : null;
                          const localHoursArray =
                            selectedLocalHour !== null
                              ? [selectedLocalHour]
                              : [];
                          if (selectedLocalHour !== null) {
                            const aggregationWindow =
                              alarmArgs.aggregation_window_hours || 1;
                            const allLocalHours = [];
                            for (let i = 0; i < 24; i += aggregationWindow) {
                              const hour = (selectedLocalHour + i) % 24;
                              allLocalHours.push(hour);
                            }
                            const allUtcHours = convertLocalToUTC(
                              allLocalHours
                            );
                            setAlarmArgs({
                              ...alarmArgs,
                              local_time_to_run: localHoursArray,
                              time_to_run: allUtcHours,
                            });
                          } else {
                            setAlarmArgs({
                              ...alarmArgs,
                              local_time_to_run: [],
                              time_to_run: [],
                            });
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            label={t("times_to_run")}
                            placeholder={t("select_hour_local")}
                            helperText={`Your timezone: ${userTimezone}`}
                          />
                        )}
                      />
                    </Tooltip>
                  </FormControl>
                </Grid>
              </>
            )}
            <Grid item xs={12} sm={6}>
              <FormControl className={classes.formControl}>
                <Tooltip title={t("also_notify_tooltip")}>
                  <Autocomplete
                    id="extra_emails"
                    name="extra_emails"
                    multiple
                    limitTags={2}
                    options={availableEmails}
                    onChange={(event, newValue) => {
                      setCurrentAlarm({
                        ...currentAlarm,
                        extra_emails: newValue,
                      });
                    }}
                    //Allow a freeform option to appear in the dropdown
                    onInputChange={(event, newValue) => {
                      setAvailableEmails([...emailList, newValue]);
                    }}
                    value={currentAlarm?.extra_emails}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label={t("also_notify")}
                      />
                    )}
                  />
                </Tooltip>
              </FormControl>
            </Grid>

            {/* Webhooks section */}
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={enableWebhooks}
                    onChange={(e) => setEnableWebhooks(e.target.checked)}
                    color="primary"
                  />
                }
                label={t("enable_webhooks")}
              />
            </Grid>
            {enableWebhooks && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl className={classes.formControl}>
                    <Autocomplete
                      multiple
                      options={webhooksList}
                      getOptionLabel={(option) => option.webhook_name}
                      value={selectedWebhooks}
                      onChange={(event, newValue) =>
                        setSelectedWebhooks(newValue)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label={t("webhooks")}
                          placeholder={t("select_webhooks")}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("webhook_payload_values")}
                    multiline
                    rows={4}
                    variant="outlined"
                    fullWidth
                    placeholder='{"key": "value"}'
                    value={webhookPayloadValues}
                    onChange={(e) => {
                      const val = e.target.value;
                      setWebhookPayloadValues(val);
                      try {
                        if (val.trim() === "") setJsonError("");
                        else {
                          JSON.parse(val);
                          setJsonError("");
                        }
                      } catch (e) {
                        setJsonError(t("invalid_json"));
                      }
                    }}
                    error={!!jsonError}
                    helperText={jsonError || t("webhook_payload_values_help")}
                  />
                </Grid>
              </>
            )}

            {SAPEnabled && (
              <Grid item xs={12} sm={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={enableSapNotification}
                      onChange={handleSapCheckboxChange}
                    />
                  }
                  label={
                    <span>
                      {t("sap_notification")}
                      <Tooltip title={t("sap_notification_tooltip")}>
                        <HelpIcon
                          style={{
                            display: "inline",
                            fontSize: 16,
                            marginLeft: 3,
                          }}
                        />
                      </Tooltip>
                    </span>
                  }
                />
              </Grid>
            )}
            {enableSapNotification && (
              <Grid item xs={12} sm={12}>
                <FormControl className={classes.notificationFormControl}>
                  <Tooltip title={t("notification_rate_tooltip")}>
                    <TextField
                      label={t("notification_rate")}
                      variant="outlined"
                      id="sap_rate_in_seconds"
                      name="sap_rate_in_seconds"
                      type="text"
                      required
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="start">
                            <Select
                              value={notificationRateUnit}
                              onChange={(event) =>
                                setNotificationRateUnit(event.target.value)
                              }
                              name="notificationRateUnit"
                            >
                              <MenuItem value="minutes">{t("min")}</MenuItem>
                              <MenuItem value="hours">{t("hrs")}</MenuItem>
                              <MenuItem value="days">{t("days")}</MenuItem>
                            </Select>
                          </InputAdornment>
                        ),
                      }}
                      inputProps={{ maxLength: 3 }}
                      value={currentAlarm?.sap_rate_in_seconds}
                      onChange={handleNotificationRateInput}
                      error={!!notificationRateError}
                      helperText={notificationRateError}
                      style={{ maxWidth: "200px" }}
                    />
                  </Tooltip>
                </FormControl>
                <FormControl className={classes.notificationFormControl}>
                  <Tooltip title={t("notification_description_tooltip")}>
                    <TextField
                      label={t("notification_description")}
                      variant="outlined"
                      id="Notification Description"
                      name="Notification Description"
                      value={sapNotificationFields.ShortText}
                      onChange={(event) =>
                        handleFieldChange("ShortText", event.target.value)
                      }
                      inputProps={{ maxLength: 40 }}
                    />
                  </Tooltip>
                </FormControl>
                <FormControl className={classes.notificationFormControl}>
                  <Tooltip title={t("functional_location_tooltip")}>
                    <TextField
                      label={t("functional_location")}
                      variant="outlined"
                      id="Functional Location"
                      name="Functional Location"
                      required
                      value={sapNotificationFields.FunctLoc}
                      onChange={(event) =>
                        handleFieldChange("FunctLoc", event.target.value)
                      }
                      inputProps={{ maxLength: 40 }}
                    />
                  </Tooltip>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={12} style={{ textAlign: "right" }}>
              <Button
                className={classes.saveButton}
                type="submit"
                variant="contained"
                color="secondary"
              >
                {t("submit")}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </form>
      <Dialog
        open={openDeleteConfirm}
        onClose={closeDeleteConfirm}
        aria-labelledby="form-dialog-title"
        fullWidth={true}
        maxWidth="sm"
      >
        <DialogTitle id="form-dialog-title">
          <WarningIcon color="secondary"></WarningIcon> {t("delete_alarm")}{" "}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Typography component="div">
              <Box fontStyle="normal" m={1}>
                {t("delete_alarm_warning")}
              </Box>
              <Box fontWeight="fontWeightBold" m={1}>
                {t("delete")} {alarm.name}?
              </Box>
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirm} className={classes.colorGray}>
            {t("cancel")}
          </Button>
          <Button
            onClick={() => {
              closeDeleteConfirm();
              onDeleteAlarm(alarm);
            }}
            color="secondary"
          >
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

AlarmForm.propTypes = {
  className: PropTypes.string,
  machine: PropTypes.object.isRequired,
};

export default AlarmForm;
